'use client'

import { Container, FlexContainer, GridArea, GridContainer, LineSeparator } from '@news12/storybook-news'

import React, { useEffect, useMemo } from 'react'

import StoriesSection from '../../components/StoriesSection/StoriesSection'
import Hero from '../../components/Story/Hero'
import MoreStories from '../../components/Story/MoreStories'
import StoryContent from '../../components/Story/StoryContent'
import StoryTitle from '../../components/Story/StoryTitle'
import useBelowBreakpoint from '../../hooks/useBelowBreakpoint'
import useStoryPageTracking from '../../hooks/useStoryPageTracking'
import { StoryPreview, TopStory } from '../../types'
import { AD_UNIT_SIZES } from '../../utils/ads/adConfig'
import extractVideoUrl from '../../utils/extractVideoUrl'
import AdUnit from '../Ads/AdUnit'
import MinutelyWidget from '../Ads/MinutelyWidget'
import Taboola from '../Ads/Taboola'
import { useAdsContext } from '../AdsProvider'
import DvdPreRollAdManager from '../DvdPreRollAdManager'
import LazyLoad from '../utilities/lazyLoad'

import { useTheme } from 'styled-components'

const gridTemplateAreasForMobile = `
  'story-title'
  'story-hero'
  'more-stories'
  'content'
`

const gridTemplateAreasForDesktop = `
  'story-hero more-stories'
  'story-title more-stories'
  'content more-stories'
  '. more-stories'
`

type StoryPageProps = {
  story: TopStory
  moreStories: StoryPreview[]
  relatedStories: StoryPreview[]
}

const StoryPage = ({ story, moreStories, relatedStories }: StoryPageProps) => {
  useStoryPageTracking(story)

  const theme = useTheme()

  const { changeCategoriesNames, changeAdsEnabled, resetAdsEnabled, resetCategoriesNames } = useAdsContext()

  const categoriesNames = useMemo(() => story.categories.map(({ name }) => `category/${name?.toLowerCase()}`), [story])

  const isBelowLg = useBelowBreakpoint('lg')
  const isBelowXxl = useBelowBreakpoint('xxl')

  const isAdsEnabled = story.withAds
  const isTaboolaEnabled = story.withTaboola && story.withAds
  const isPreRollEnabled = story.withPreRoll && story.withAds
  const isMoreStoriesExist = Boolean(moreStories.length)
  const heroExists = extractVideoUrl(story?.videoFile?.videoUrls) || story?.heroImage

  const isSidebarVisible = (heroExists || !isBelowLg) && (isMoreStoriesExist || isAdsEnabled)

  useEffect(() => {
    if (!isAdsEnabled) {
      const browsiTags = document.querySelectorAll('script[id="browsi-tag"]')
      browsiTags.forEach(tag => tag.remove())
      const browsiAdContainers = document.querySelectorAll('[id^="browsi_adContainer_ai"]')
      browsiAdContainers.forEach(el => el.parentNode?.removeChild(el))
    }
  }, [isAdsEnabled])

  useEffect(() => {
    changeCategoriesNames(categoriesNames)

    return () => resetCategoriesNames()
  }, [categoriesNames])

  useEffect(() => {
    changeAdsEnabled(isAdsEnabled)

    return () => resetAdsEnabled()
  }, [isAdsEnabled])

  const sidebarAdUnitSizes = useMemo(
    () => ({
      height: 'auto',
      minHeight: '250px'
    }),
    []
  )

  return (
    <>
      <DvdPreRollAdManager isEnabled={story.withDvdPreRoll} />
      {isAdsEnabled && (
        <Container
          fullWidth
          backgroundColor={isBelowLg ? 'transparent' : `${theme.colors.mist}80`}
          margin={['1rem 0', '0 0 1.5rem']}
          padding={{ default: 0, lg: '0.5rem 0' }}>
          <FlexContainer justifyContent='center'>
            <AdUnit
              adUnitTemplate='leaderboard'
              adUnitSizes={AD_UNIT_SIZES}
              contentfulCategories={categoriesNames}
              id='ad-banner-top'
              type='leaderboardRedesign'
            />
          </FlexContainer>
        </Container>
      )}
      <Container
        padding={{
          default: isAdsEnabled ? '0 1rem' : '0.75rem 1rem 0',
          lg: isAdsEnabled ? '0 3.75rem' : '1.5rem 3.75rem 0'
        }}>
        <GridContainer
          gridTemplateAreas={{ default: gridTemplateAreasForMobile, lg: gridTemplateAreasForDesktop }}
          gridTemplateColumns={{
            default: 'minmax(0, 1fr)',
            lg: isSidebarVisible ? 'minmax(0, 1fr) 407px' : 'minmax(0, 1fr)'
          }}>
          <GridArea
            className='story-title'
            gridArea='story-title'
            margin={{ default: !heroExists ? '0 0 1.5rem' : '0', lg: '0 0 2.5rem' }}>
            <StoryTitle story={story} />
            {isAdsEnabled && !isBelowXxl && (
              <FlexContainer justifyContent='center' margin='2.5rem 0 0'>
                <AdUnit
                  adUnitSizes={{
                    height: { default: '90px' },
                    width: { default: '728px' }
                  }}
                  contentfulCategories={categoriesNames}
                  id='ad-story-byline-large'
                  type='storyPageByline'
                />
              </FlexContainer>
            )}
            {isAdsEnabled && !isBelowLg && isBelowXxl && (
              <FlexContainer justifyContent='center' margin='2.5rem 0 0'>
                <AdUnit
                  adUnitSizes={{
                    height: { default: '50px' },
                    minWidth: { default: '300px' },
                    maxWidth: { default: '320px' }
                  }}
                  contentfulCategories={categoriesNames}
                  id='ad-story-byline-medium'
                  type='storyPageByline'
                />
              </FlexContainer>
            )}
          </GridArea>
          {isSidebarVisible && (
            <GridArea gridArea='more-stories' margin={{ default: '0 0 1.5rem', lg: '0 0 0 2.5rem' }}>
              {isAdsEnabled && !isBelowLg && (
                <FlexContainer margin='0 0 2.5rem'>
                  <AdUnit
                    adUnitTemplate='rectangleVertical'
                    adUnitSizes={sidebarAdUnitSizes}
                    contentfulCategories={categoriesNames}
                    id='ad-banner-sidebar-top'
                    type='sidebar1'
                  />
                </FlexContainer>
              )}
              {isMoreStoriesExist && (
                <Container
                  fullWidth
                  position='relative'
                  top={{ xl: '3rem' }}
                  padding={{ default: '3.5rem 1rem 1.5rem', lg: '0' }}
                  margin={{ default: '-4rem -1rem 0', lg: '0' }}
                  backgroundColor={isBelowLg ? `${theme.colors.mist}80` : 'transparent'}>
                  <MoreStories stories={moreStories} />
                </Container>
              )}
              {isAdsEnabled && isBelowLg && (
                <FlexContainer justifyContent='center' margin='1rem 0 0'>
                  <AdUnit
                    adUnitSizes={{
                      height: { default: '50px' },
                      minWidth: { default: '300px' },
                      maxWidth: { default: '320px' }
                    }}
                    contentfulCategories={categoriesNames}
                    id='ad-story-byline-small'
                    type='storyPageByline'
                  />
                </FlexContainer>
              )}
              {isAdsEnabled && isBelowLg && <LineSeparator margin='1rem auto' />}
            </GridArea>
          )}
          {heroExists && (
            <GridArea gridArea='story-hero' margin={{ default: '1rem 0 2rem', lg: '0 0 1rem' }}>
              <Hero story={story} withAds={isPreRollEnabled} className='story-page-hero' />
            </GridArea>
          )}
          <GridArea gridArea='content'>
            <StoryContent story={story} />
          </GridArea>
        </GridContainer>
      </Container>
      <Container>
        <LineSeparator margin='1.5rem auto' />
        {isAdsEnabled && (
          <>
            <FlexContainer justifyContent='center'>
              <AdUnit
                adUnitTemplate='leaderboard'
                contentfulCategories={categoriesNames}
                id='ad-banner-end-story'
                type='leaderboardDynamic'
              />
            </FlexContainer>
            <LineSeparator margin='1.5rem auto' />
          </>
        )}
      </Container>
      <Container padding={{ default: 0, lg: '0 1rem' }}>
        <StoriesSection
          adUnitCategories={categoriesNames}
          category='More from News 12'
          stories={relatedStories}
          shouldShowAd={isAdsEnabled}
          fullWidthBreakpoint='lg'
        />
      </Container>
      {isAdsEnabled && (
        <Container>
          <LineSeparator margin='1.5rem auto' />
          <FlexContainer alignItems='center' direction='column' gap='1.25rem'>
            <MinutelyWidget />
            {isTaboolaEnabled && (
              <LazyLoad>
                <Taboola />
              </LazyLoad>
            )}
          </FlexContainer>
        </Container>
      )}
    </>
  )
}

export default StoryPage
